/**
 * DrawingTypes.ts
 * -----------------------------------------------------------------------------
 * Minimal, self-contained drawing model consumed by Canvas2DRenderer. A
 * `DrawingPage` is a flat list of styled primitive shapes in page coordinates
 * (PDF-point convention: origin top-left, y increases downward).
 *
 * Colours are `Rgb` triples with each component in 0..1 so the renderer can
 * map them straight to CSS rgb() and (eventually) PDF colour ops.
 */

/** RGB colour, each component 0..1. */
export type Rgb = [number, number, number];

export interface StrokeStyle {
  color?: Rgb;
  /** Line weight in PDF points (used for export; on-screen draws at ~1px). */
  width?: number;
  /** Stroke width in WORLD units (mm). When set, the on-screen stroke is drawn
   *  at this true width with round joins/caps (e.g. a rebar at its diameter)
   *  instead of the constant 1px CAD line. */
  worldWidth?: number;
  /** Stroke width in SCREEN pixels. Unlike `worldWidth`, this stays the same
   *  weight at every zoom — an annotation drawn OVER the drawing (the bridge
   *  alignment on an elevation) rather than a thing being measured. Ignored
   *  when `worldWidth` is set. */
  screenWidth?: number;
  /** Dash pattern in PDF points; empty/undefined = solid. */
  dash?: number[];
}

export interface FillStyle {
  fill?: Rgb | null;
  stroke?: Rgb | null;
  width?: number;
  dash?: number[];
  /** Outline width in SCREEN pixels — the filled-shape counterpart of
   *  StrokeStyle.screenWidth, so a section profile can stay heavier than the
   *  dimension rails around it at every zoom. */
  screenWidth?: number;
  /** Rule the interior with parallel lines instead of (or as well as) a solid
   *  fill — concrete, the way a section drawing hatches it. */
  hatch?: HatchStyle;
}

/** A ruled fill: parallel lines at `spacing` world units, `angle` degrees.
 *  Mirrors an SVG <pattern> of hatch lines, which is where these come from
 *  (see io/SvgImport.ts). */
export interface HatchStyle {
  /** Line-to-line distance in world units. */
  spacing: number;
  /** Direction in degrees, measured the SVG way: clockwise from +x, because
   *  page y runs downward. */
  angle: number;
  color?: Rgb;
}

export interface TextStyle {
  color?: Rgb;
  size?: number; // pt
  bold?: boolean;
  anchor?: 'start' | 'middle' | 'end';
  /** Vertical alignment of the text relative to its y. Default 'top' (the y is
   *  the top of the glyph cell). 'bottom' = the text sits above the y. */
  valign?: 'top' | 'middle' | 'bottom';
  /** Rotation in degrees, counter-clockwise about the (x, y) anchor point.
   *  Default 0. Used e.g. for vertical dimension values. */
  rotate?: number;
  /** Set a monospaced face — dimension values, where tabular figures line up
   *  and a drawing reads as a drawing. Default is the sans stack. */
  mono?: boolean;
}

export interface LineShape {
  kind: 'line';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  style?: StrokeStyle;
}

export interface RectShape {
  kind: 'rect';
  x: number;
  y: number;
  w: number;
  h: number;
  style?: FillStyle;
}

export interface PolygonShape {
  kind: 'polygon';
  points: ReadonlyArray<readonly [number, number]>;
  /** Optional hole rings (even-odd filled) — e.g. an elevator core shaft. */
  holes?: ReadonlyArray<ReadonlyArray<readonly [number, number]>>;
  style?: FillStyle;
}

export interface PolylineShape {
  kind: 'polyline';
  points: ReadonlyArray<readonly [number, number]>;
  style?: StrokeStyle;
}

export interface CircleShape {
  kind: 'circle';
  cx: number;
  cy: number;
  r: number;
  style?: FillStyle;
}

export interface TextShape {
  kind: 'text';
  x: number;
  y: number;
  text: string;
  style?: TextStyle;
}

export type DrawShape =
  | LineShape
  | RectShape
  | PolygonShape
  | PolylineShape
  | CircleShape
  | TextShape;

/**
 * One dimension as a MEASUREMENT rather than as the lines that draw it.
 *
 * Mesh2DExporter explodes every dimension into witness lines, arrowheads, a rule
 * and a text run, because that is what a canvas can draw — and there is no way
 * back to the measurement from the loose lines. CAD wants the measurement: a
 * DIMENSION entity that can be re-styled, re-measured and picked as one object.
 * So the exporter records it here as it drafts, and `from`..`to` (a half-open
 * range into `shapes`) says which shapes it produced.
 *
 * Only `pageToDxf` reads these — the canvas renderer and the SVG exporter draw
 * the shapes and ignore them, so the preview and the SVG are unaffected.
 *
 * Page coordinates throughout (y DOWN), like the shapes.
 */
export interface DimRecord {
  /** Index of the first shape this dimension drafted. */
  from: number;
  /** One past the last shape this dimension drafted. */
  to: number;
  /** First measured point — where its first witness line starts. */
  x1: number;
  y1: number;
  /** Second measured point. */
  x2: number;
  y2: number;
  /** A point ON the dimension line, i.e. its offset from the feature. */
  lineX: number;
  lineY: number;
  /** The value as lettered. Written to the DXF as a text OVERRIDE rather than
   *  left for CAD to measure: page units are plot millimetres, so a view drawn
   *  at 1:200 would otherwise come back measuring a two-hundredth of the
   *  structure. */
  text: string;
  /** True when the dimension line runs vertically. */
  vertical?: boolean;
}

/**
 * One VIEW on a composed sheet: the drawing and the title block under it, taken
 * together as the one thing a reader picks up.
 *
 * Written by Mesh2DExporter.ExportSheetJson, which is the only place that knows
 * both halves of a view and where each of them landed. Everything else about a
 * page is a flat list of shapes; this is what lets the sheet be drafted by hand
 * — drag a view somewhere else, pin it to a scale of its own — without the
 * renderer having to understand a drawing.
 */
export interface SheetView {
  /** Stable across redraws: the view's caption, numbered where a sheet carries
   *  two of a name. What an adjustment to it is filed under in C#. */
  id: string;
  caption: string;
  /** The view's box in page coordinates — drawing and title block unioned. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Half-open ranges into `shapes`: the drawing, then its title block. Two of
   *  them because a row's titles are drafted after all of its drawings. */
  spans: ReadonlyArray<readonly [number, number]>;
  /** What it is drawn at — 1:`scale`. Null on a view drawn to no scale. */
  scale?: number | null;
  /** The reader pinned that ratio, rather than the composer choosing it. */
  pinned?: boolean;
  /** The reader has dragged this view off where the composer put it. */
  moved?: boolean;
}

export interface DrawingPage {
  paperWidth: number; // page extents in PDF points
  paperHeight: number;
  shapes: DrawShape[];
  /** The page's dimensions, as measurements. Absent on a page with none, and on
   *  one whose dimensions were drafted without a measurement behind them (an arc
   *  length along the alignment). See DimRecord. */
  dims?: DimRecord[];
  /** Page coordinates of the model origin (0,0), so a renderer can draw a
   *  model-aligned coordinate grid + axis. Page units equal model mm. */
  origin?: { x: number; y: number };
  /** Optional sub-rectangle (page coords) the renderer should fit-to-window to
   *  instead of the whole page — used to zoom to an isolated selection. */
  fitRect?: { x: number; y: number; w: number; h: number };
  /** The views this page was composed from, for a host that lets the reader lay
   *  the sheet out by hand. Absent on every page that is a single drawing. */
  views?: SheetView[];
  /** 'mm' on a composed sheet, whose page units are millimetres of paper: the
   *  renderer then thins its pens as the sheet is zoomed below print size.
   *  Absent on a single drawing, which is in model or picture units. */
  paperUnit?: 'mm';
}

/** Shared palette so SVG and canvas previews look identical. */
export const COLOR = {
  ink: [0.08, 0.1, 0.13] as Rgb,
};

/** Default ink colour for shapes that don't specify one. */
export const INK: Rgb = COLOR.ink;
